import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Camera, Heart, MessageCircle, Share2, Link2 } from "lucide-react";
import UserLink from "./UserLink";
import PhotoViewerModal from "./PhotoViewerModal";
import parseCaption from "../utils/parseCaption";
import timeAgo from "../utils/timeAgo";
import { T } from "../theme";

/**
 * PhotoFeedCard — renders a photo_post activity event in any feed context
 * (FriendsPage feed, UserDashboard stage feed).
 *
 * Standalone and reusable: everything it needs rides on the activity object
 * the feed endpoint returns (which embeds the full photo — kandi beads, like
 * state — so no second fetch). Opening the photo always goes through
 * PhotoViewerModal; mentions always render via parseCaption.
 *
 * Props:
 *   activity – photo_post event from /api/friends/:userId/feed
 *   apiUrl   – API base for image srcs and like calls
 *   viewerId – logged-in user id (enables liking); optional
 *   friends  – friend list for @mention userId resolution; optional
 */


function ActorAvatar({ username, avatar, size = 34 }) {
  if (avatar) {
    return (
      <img
        src={avatar}
        alt=""
        style={{
          width: size, height: size, borderRadius: "50%", objectFit: "cover",
          flexShrink: 0, border: "2px solid rgba(255,255,255,0.15)",
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg, ${T.purple}, ${T.pink})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.34, fontWeight: 800, color: "#fff",
      border: "2px solid rgba(255,255,255,0.15)",
    }}>
      {username?.slice(0, 2).toUpperCase() || "??"}
    </div>
  );
}

function PhotoFeedCard({ activity, apiUrl = "", viewerId = null, friends = [] }) {
  const actor = activity.user || {};
  const username = activity.actorUsername || actor.username;
  const caption = activity.caption ?? activity.message ?? "";

  // Full photo object embedded by the feed endpoint; fall back to the event
  // payload if the photo has since been deleted (imageUrl still cached on it).
  const photo = activity.photo || {
    id:            activity.photoId,
    userId:        actor.id,
    imageUrl:      activity.photoUrl || activity.thumbnailUrl,
    message:       caption,
    kandiId:       activity.kandiId,
    kandiName:     activity.kandiName,
    kandi:         null,
    likeCount:     0,
    likedByViewer: false,
    createdAt:     activity.createdAt,
  };

  const [likes, setLikes] = useState({
    count: photo.likeCount || 0,
    liked: !!photo.likedByViewer,
  });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Nothing renderable — photo deleted and no cached URL. Skip gracefully.
  if (!photo.imageUrl) return null;

  const kandiName = photo.kandi?.name || activity.kandiName;
  const imgSrc = photo.imageUrl.startsWith("http") ? photo.imageUrl : `${apiUrl}${photo.imageUrl}`;

  const showToast = (msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };

  const toggleLike = async () => {
    if (!viewerId) { showToast("Log in to send some PLUR 💖"); return; }
    if (!photo.id) return;
    const prev = likes;
    setLikes({ count: prev.count + (prev.liked ? -1 : 1), liked: !prev.liked });
    try {
      const res = await axios.post(`${apiUrl}/api/photos/${photo.id}/like`, { userId: viewerId });
      if (res.data.success) setLikes({ count: res.data.likeCount, liked: res.data.liked });
    } catch (err) {
      console.error("Like error:", err);
      setLikes(prev);
    }
  };

  const sharePhoto = async () => {
    const link = photo.imageUrl.startsWith("http")
      ? photo.imageUrl
      : `${apiUrl || window.location.origin}${photo.imageUrl}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Link copied");
    } catch {
      showToast("Couldn't copy link");
    }
  };

  // Keep the modal's seed in sync with any likes toggled on the card
  const photoForViewer = { ...photo, likeCount: likes.count, likedByViewer: likes.liked };

  const footBtn = (active) => ({
    display: "flex", alignItems: "center", gap: 6,
    padding: "7px 12px", minHeight: 34, borderRadius: 10,
    background: active ? "rgba(255,60,172,0.14)" : "rgba(255,255,255,0.04)",
    border: active ? "1px solid rgba(255,60,172,0.4)" : "1px solid rgba(255,255,255,0.08)",
    color: active ? T.pink : "rgba(255,255,255,0.65)",
    fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
    transition: "all 0.15s",
  });

  return (
    <div style={{
      background: T.card,
      backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      border: T.border, borderRadius: T.radius,
      padding: "14px 14px 12px",
      marginBottom: 10,
    }}>
      {/* ── Header row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <ActorAvatar username={username} avatar={activity.actorAvatar || actor.profilePic} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: "0.86rem", lineHeight: 1.35 }}>
            {username
              ? <UserLink username={username} userId={activity.actorId || actor.id} style={{ fontSize: "0.86rem" }} />
              : <span style={{ color: T.textPrimary, fontWeight: 700 }}>Someone</span>}
            {" "}
            <span style={{ color: "rgba(255,255,255,0.6)" }}>shared a photo</span>
            <Camera size={12} color={T.photoPink} style={{
              marginLeft: 6, verticalAlign: -2,
              filter: `drop-shadow(0 0 4px ${T.photoPink})`,
            }} />
          </p>
          {kandiName && (
            <p style={{
              margin: "2px 0 0", color: T.textMuted, fontSize: "0.73rem",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              <Link2 size={9} style={{ verticalAlign: -1, marginRight: 3 }} />
              featuring {kandiName}
            </p>
          )}
        </div>
        <span style={{ color: T.textMuted, fontSize: "0.7rem", flexShrink: 0 }}>
          {timeAgo(activity.createdAt)}
        </span>
      </div>

      {/* ── Photo thumbnail — opens PhotoViewerModal ── */}
      <img
        src={imgSrc}
        alt={caption || "Photo"}
        onClick={() => setViewerOpen(true)}
        style={{
          display: "block", width: "100%", maxHeight: 240,
          objectFit: "cover", borderRadius: 14, cursor: "pointer",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      />

      {/* ── Caption preview — 2 lines max, full text in the viewer ── */}
      {caption && (
        <p
          onClick={() => setViewerOpen(true)}
          style={{
            margin: "10px 0 0", color: "rgba(255,255,255,0.78)",
            fontSize: "0.84rem", lineHeight: 1.45, cursor: "pointer",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {parseCaption(caption, friends)}
        </p>
      )}

      {/* ── Footer row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <button onClick={toggleLike} style={footBtn(likes.liked)} aria-label="Like photo">
          <Heart size={13} fill={likes.liked ? T.pink : "none"} />
          {likes.count > 0 ? likes.count : "Like"}
        </button>
        <span style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "7px 12px", color: T.textMuted, fontSize: "0.76rem", fontWeight: 700,
        }}>
          <MessageCircle size={13} />
          {photo.commentCount || 0}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={sharePhoto} style={footBtn(false)} aria-label="Share photo">
          <Share2 size={13} /> Share
        </button>
      </div>

      {/* ── Full viewer ── */}
      {viewerOpen && (
        <PhotoViewerModal
          photos={[photoForViewer]}
          initialIndex={0}
          apiUrl={apiUrl}
          viewerId={viewerId}
          friends={friends}
          onClose={() => setViewerOpen(false)}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div
          className="pvm-toast"
          style={{
            position: "fixed", bottom: "calc(28px + env(safe-area-inset-bottom))",
            left: "50%", transform: "translateX(-50%)",
            padding: "10px 20px", borderRadius: 30, zIndex: 310,
            background: "rgba(16,8,32,0.95)",
            border: "1px solid rgba(0,245,255,0.35)",
            color: T.textPrimary, fontSize: "0.82rem", fontWeight: 700,
            boxShadow: "0 8px 28px rgba(0,0,0,0.5), 0 0 18px rgba(0,245,255,0.15)",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default PhotoFeedCard;
